/**
 * Export Preview Component
 * Shows preview of messages with smart filtering and interactive selection
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import type {
  ExportFilters,
  ExportPreviewItem,
  UserActivityLevel,
  ConversationDepth,
  MessagePosition,
  UserStage,
} from '@/types/annotation';
import {
  Search,
  Download,
  Eye,
  Filter,
  Check,
  X,
  AlertTriangle,
  Hash,
  Copy,
  Users,
  MessageCircle,
  Clock,
  Sparkles,
  RefreshCw,
} from 'lucide-react';

// Filter option labels
const USER_ACTIVITY_LABELS: Record<UserActivityLevel, string> = {
  single: '单次用户',
  light: '轻度用户(2-5次)',
  medium: '中度用户(6-20次)',
  heavy: '重度用户(20+次)',
};

const CONVERSATION_DEPTH_LABELS: Record<ConversationDepth, string> = {
  single_turn: '单轮对话',
  short: '短对话(2-3轮)',
  medium: '中等(4-10轮)',
  deep: '深度对话(10+轮)',
};

const MESSAGE_POSITION_LABELS: Record<MessagePosition, string> = {
  first: '首轮消息',
  early: '前期(2-3轮)',
  middle: '中期消息',
  last: '末轮消息',
};

const USER_STAGE_LABELS: Record<UserStage, string> = {
  new: '新用户(0-1天)',
  recent: '近期(2-7天)',
  active: '活跃(8-30天)',
  veteran: '老用户(30+天)',
};

// Preset configurations
const PRESETS = {
  balanced: {
    name: '均衡采样',
    description: '从各类用户和对话中均衡抽取',
    filters: {
      exclude_template_messages: true,
      random_sample: true,
    },
  },
  deep_conversations: {
    name: '深度对话',
    description: '来自多轮深入交流的消息',
    filters: {
      conversation_depths: ['medium', 'deep'] as ConversationDepth[],
      message_positions: ['middle', 'last'] as MessagePosition[],
      exclude_template_messages: true,
      random_sample: true,
    },
  },
  new_users: {
    name: '新用户体验',
    description: '新用户的首次交互',
    filters: {
      user_stages: ['new', 'recent'] as UserStage[],
      message_positions: ['first', 'early'] as MessagePosition[],
      exclude_template_messages: true,
      random_sample: true,
    },
  },
  active_users: {
    name: '活跃用户',
    description: '来自高频使用用户的消息',
    filters: {
      user_activity_levels: ['medium', 'heavy'] as UserActivityLevel[],
      exclude_template_messages: true,
      random_sample: true,
    },
  },
  emotional_content: {
    name: '情感表达',
    description: '包含情感关键词的消息',
    filters: {
      include_keywords: ['焦虑', '担心', '开心', '难过', '纠结', '迷茫', '压力', '累'],
      exclude_template_messages: true,
      random_sample: true,
    },
  },
};

interface ExportPreviewProps {
  onPreview: (filters: ExportFilters, sampleSize: number, offset: number, excludeExported: boolean) => Promise<void>;
  onExecute: (
    filters: ExportFilters,
    sampleSize: number,
    title: string,
    description?: string,
    selectedIds?: string[],
    skipDuplicates?: boolean
  ) => Promise<void>;
  previewData?: {
    total_available: number;
    sample: ExportPreviewItem[];
  };
  isLoading?: boolean;
  isExecuting?: boolean;
}

export function ExportPreview({
  onPreview,
  onExecute,
  previewData,
  isLoading = false,
  isExecuting = false,
}: ExportPreviewProps) {
  // Filter state
  const [filters, setFilters] = useState<ExportFilters>({
    min_message_length: 15,
    max_message_length: 300,
    exclude_patterns: [],
    include_keywords: [],
    user_activity_levels: [],
    user_stages: [],
    conversation_depths: [],
    message_positions: [],
    exclude_template_messages: true,
    random_sample: true,
  });
  const [sampleSize, setSampleSize] = useState(100);
  const [offset, setOffset] = useState(0);
  const [excludeExported, setExcludeExported] = useState(true);
  const [batchTitle, setBatchTitle] = useState('');
  const [batchDescription, setBatchDescription] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  // Filtered sample based on search
  const filteredSample = useMemo(() => {
    if (!previewData?.sample) return [];
    let items = previewData.sample;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item) => item.text.toLowerCase().includes(query));
    }

    return items;
  }, [previewData?.sample, searchQuery]);

  // Calculate selection stats
  const selectionStats = useMemo(() => {
    if (!previewData?.sample) return { total: 0, selected: 0, duplicates: 0, selectedDuplicates: 0 };

    const duplicates = previewData.sample.filter((item) => item.is_duplicate).length;
    const selectedDuplicates = previewData.sample.filter(
      (item) => item.is_duplicate && selectedIds.has(item.id)
    ).length;

    return {
      total: previewData.sample.length,
      selected: selectedIds.size,
      duplicates,
      selectedDuplicates,
    };
  }, [previewData?.sample, selectedIds]);

  // Effective selection count
  const effectiveSelectionCount = useMemo(() => {
    if (selectedIds.size === 0) return previewData?.sample?.length || 0;
    if (skipDuplicates) {
      return selectionStats.selected - selectionStats.selectedDuplicates;
    }
    return selectionStats.selected;
  }, [selectedIds.size, skipDuplicates, selectionStats, previewData?.sample?.length]);

  const handlePreview = (newOffset: number = 0) => {
    setSelectedIds(new Set());
    setOffset(newOffset);
    onPreview(filters, sampleSize, newOffset, excludeExported);
  };

  const handleExecute = () => {
    if (!batchTitle.trim()) {
      alert('请输入批次标题');
      return;
    }

    const idsToExport = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    onExecute(filters, sampleSize, batchTitle, batchDescription || undefined, idsToExport, skipDuplicates);
  };

  const applyPreset = (presetKey: string) => {
    const preset = PRESETS[presetKey as keyof typeof PRESETS];
    if (preset) {
      // Reset to defaults first, then apply preset filters
      setFilters({
        ...DEFAULT_FILTERS,
        ...preset.filters,
      });
      setActivePreset(presetKey);
      setKeywordInput('');
    }
  };

  // Default filter values (moved before applyPreset for reference)
  const DEFAULT_FILTERS: ExportFilters = {
    min_message_length: 15,
    max_message_length: 300,
    exclude_patterns: [],
    include_keywords: [],
    user_activity_levels: [],
    user_stages: [],
    conversation_depths: [],
    message_positions: [],
    exclude_template_messages: true,
    random_sample: true,
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim()) {
      setFilters((prev) => ({
        ...prev,
        include_keywords: [...(prev.include_keywords || []), keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  const toggleMultiSelect = <T extends string>(
    field: keyof ExportFilters,
    value: T,
    currentValues: T[] = []
  ) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    setFilters((prev) => ({ ...prev, [field]: newValues }));
    setActivePreset(null);
  };

  // Reset all filters to defaults
  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setActivePreset(null);
    setKeywordInput('');
  };

  // Check if any filters are active (different from defaults)
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.user_activity_levels?.length || 0) > 0 ||
      (filters.user_stages?.length || 0) > 0 ||
      (filters.conversation_depths?.length || 0) > 0 ||
      (filters.message_positions?.length || 0) > 0 ||
      (filters.include_keywords?.length || 0) > 0 ||
      (filters.exclude_patterns?.length || 0) > 0 ||
      filters.min_message_length !== 15 ||
      filters.max_message_length !== 300
    );
  }, [filters]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    const allIds = new Set(filteredSample.map((item) => item.id));
    setSelectedIds(allIds);
  };

  const selectNonDuplicates = () => {
    const nonDuplicateIds = new Set(
      filteredSample.filter((item) => !item.is_duplicate).map((item) => item.id)
    );
    setSelectedIds(nonDuplicateIds);
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const copyHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
  };

  const getMetadataBadge = (item: ExportPreviewItem) => {
    const badges = [];
    if (item.user_activity_level) {
      badges.push({ label: USER_ACTIVITY_LABELS[item.user_activity_level], color: 'bg-blue-100 text-blue-700' });
    }
    if (item.conversation_depth) {
      badges.push({ label: CONVERSATION_DEPTH_LABELS[item.conversation_depth], color: 'bg-purple-100 text-purple-700' });
    }
    if (item.message_position) {
      badges.push({ label: MESSAGE_POSITION_LABELS[item.message_position], color: 'bg-green-100 text-green-700' });
    }
    return badges;
  };

  return (
    <div className="space-y-6">
      {/* Preset Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          快速选择采样策略
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className={`
                p-3 rounded-lg border text-left transition-all
                ${activePreset === key
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              <div className="font-medium text-sm text-gray-900">{preset.name}</div>
              <div className="text-xs text-gray-500 mt-1">{preset.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5" />
            筛选条件
            {hasActiveFilters && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                已设置
              </span>
            )}
          </h3>
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                重置筛选
              </button>
            )}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {showAdvanced ? '收起高级选项' : '展开高级选项'}
            </button>
          </div>
        </div>

        {/* User Dimension Filters */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Users className="w-4 h-4" />
              用户活跃度
            </label>
            <div className="flex flex-wrap gap-2">
              {(Object.entries(USER_ACTIVITY_LABELS) as [UserActivityLevel, string][]).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => toggleMultiSelect('user_activity_levels', value, filters.user_activity_levels)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    filters.user_activity_levels?.includes(value)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              对话深度
            </label>
            <div className="flex flex-wrap gap-2">
              {(Object.entries(CONVERSATION_DEPTH_LABELS) as [ConversationDepth, string][]).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => toggleMultiSelect('conversation_depths', value, filters.conversation_depths)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    filters.conversation_depths?.includes(value)
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              用户阶段
            </label>
            <div className="flex flex-wrap gap-2">
              {(Object.entries(USER_STAGE_LABELS) as [UserStage, string][]).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => toggleMultiSelect('user_stages', value, filters.user_stages)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    filters.user_stages?.includes(value)
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">消息位置</label>
            <div className="flex flex-wrap gap-2">
              {(Object.entries(MESSAGE_POSITION_LABELS) as [MessagePosition, string][]).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => toggleMultiSelect('message_positions', value, filters.message_positions)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    filters.message_positions?.includes(value)
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">消息长度</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={filters.min_message_length}
                    onChange={(e) => setFilters((prev) => ({ ...prev, min_message_length: parseInt(e.target.value) || 10 }))}
                    min={1}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <span className="text-gray-500">至</span>
                  <input
                    type="number"
                    value={filters.max_message_length}
                    onChange={(e) => setFilters((prev) => ({ ...prev, max_message_length: parseInt(e.target.value) || 500 }))}
                    min={1}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <span className="text-gray-500 text-sm">字符</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">加载数量</label>
                <input
                  type="number"
                  value={sampleSize}
                  onChange={(e) => setSampleSize(parseInt(e.target.value) || 100)}
                  min={1}
                  max={500}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <span className="text-gray-500 text-sm ml-2">(最多500)</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">包含关键词</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="输入关键词..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                />
                <Button variant="secondary" size="sm" onClick={handleAddKeyword}>
                  添加
                </Button>
              </div>
              {filters.include_keywords && filters.include_keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filters.include_keywords.map((kw, idx) => (
                    <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
                      {kw}
                      <button
                        onClick={() => setFilters((prev) => ({
                          ...prev,
                          include_keywords: prev.include_keywords?.filter((_, i) => i !== idx),
                        }))}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.exclude_template_messages}
                  onChange={(e) => setFilters((prev) => ({ ...prev, exclude_template_messages: e.target.checked }))}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">排除模板消息</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.random_sample}
                  onChange={(e) => setFilters((prev) => ({ ...prev, random_sample: e.target.checked }))}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  随机采样
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Sample Size and Load Button */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-700">每页数量:</label>
              <div className="flex items-center gap-2">
                {[50, 100, 200, 500].map((size) => (
                  <button
                    key={size}
                    onClick={() => setSampleSize(size)}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                      sampleSize === size
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeExported}
                  onChange={(e) => setExcludeExported(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">只显示未导出的</span>
              </label>
              <Button variant="primary" onClick={() => handlePreview(0)} loading={isLoading}>
                <Eye className="w-4 h-4 mr-2" />
                加载消息
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Results with Selection */}
      {previewData && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {/* Header with Stats */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Search className="w-5 h-5" />
              选择消息
            </h3>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                已加载 <strong className="text-gray-900">{previewData.sample.length}</strong> / {previewData.total_available} 条
              </span>
              {selectionStats.duplicates > 0 && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {selectionStats.duplicates} 条已存在
                </span>
              )}
            </div>
          </div>

          {/* Search and Selection Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索消息内容..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={selectAll}>
                <Check className="w-4 h-4 mr-1" />
                全选
              </Button>
              <Button variant="ghost" size="sm" onClick={selectNonDuplicates}>
                选择非重复
              </Button>
              <Button variant="ghost" size="sm" onClick={deselectAll}>
                <X className="w-4 h-4 mr-1" />
                清除
              </Button>
            </div>
          </div>

          {/* Skip Duplicates Toggle */}
          <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="skipDuplicates"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="skipDuplicates" className="text-sm text-gray-700">
              自动跳过已标注的重复消息
            </label>
          </div>

          {/* Selection Summary */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-4 mb-4 p-3 bg-primary-50 rounded-lg">
              <span className="text-sm text-primary-700">
                已选择: <strong>{selectionStats.selected}</strong> 条
                {selectionStats.selectedDuplicates > 0 && skipDuplicates && (
                  <span className="text-amber-600"> (其中 {selectionStats.selectedDuplicates} 条重复将被跳过)</span>
                )}
              </span>
              <span className="text-sm text-primary-700">
                实际导出: <strong>{effectiveSelectionCount}</strong> 条
              </span>
            </div>
          )}

          {/* Message List */}
          <div className="space-y-2 max-h-[500px] overflow-y-auto mb-6">
            {filteredSample.map((item) => {
              const isSelected = selectedIds.has(item.id);
              const badges = getMetadataBadge(item);

              return (
                <div
                  key={item.id}
                  className={`
                    p-3 rounded-lg border cursor-pointer transition-colors
                    ${isSelected ? 'bg-primary-50 border-primary-300' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}
                    ${item.is_duplicate ? 'opacity-70' : ''}
                  `}
                  onClick={() => toggleSelection(item.id)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-4 h-4 text-primary-600 rounded"
                    />

                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 text-sm">{item.text}</p>

                      {/* Metadata Badges */}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {badges.slice(0, 2).map((badge, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded-full text-xs ${badge.color}`}>
                            {badge.label}
                          </span>
                        ))}
                        <span className="text-xs text-gray-400">{item.char_count}字</span>
                        <span
                          className="text-xs text-gray-400 flex items-center gap-0.5 cursor-pointer hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyHash(item.text_hash);
                          }}
                          title="复制 MD5"
                        >
                          <Hash className="w-3 h-3" />
                          {item.text_hash.slice(0, 6)}
                          <Copy className="w-3 h-3" />
                        </span>
                        {item.is_duplicate && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            已存在
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredSample.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchQuery ? '未找到匹配的消息' : '没有可用的消息'}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {previewData.total_available > 0 && (
            <div className="flex items-center justify-between py-4 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                显示 {offset + 1} - {Math.min(offset + previewData.sample.length, previewData.total_available)} / 共 {previewData.total_available} 条
                {excludeExported && <span className="ml-1 text-green-600">(已排除已导出)</span>}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreview(Math.max(0, offset - sampleSize))}
                  disabled={offset === 0 || isLoading}
                >
                  上一页
                </Button>
                <span className="text-sm text-gray-600 px-2">
                  第 {Math.floor(offset / sampleSize) + 1} 页
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreview(offset + sampleSize)}
                  disabled={offset + previewData.sample.length >= previewData.total_available || isLoading}
                >
                  下一页
                </Button>
              </div>
            </div>
          )}

          {/* Create Batch Form */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="font-medium text-gray-900 mb-4">创建标注批次</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  批次标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={batchTitle}
                  onChange={(e) => setBatchTitle(e.target.value)}
                  placeholder="例如: 情绪标注 - 2024年12月"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">描述 (可选)</label>
                <textarea
                  value={batchDescription}
                  onChange={(e) => setBatchDescription(e.target.value)}
                  placeholder="批次描述..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleExecute}
                  loading={isExecuting}
                  disabled={!batchTitle.trim() || effectiveSelectionCount === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  创建批次 ({selectedIds.size > 0 ? effectiveSelectionCount : previewData.sample.length} 条)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
